import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, CheckCircle2, Percent, Trash2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI, logout } from '../lib/adminAPI';
import { useRowSelection, DeleteSelectedBar } from '../components/admin/RowSelection';

const INTENT_LABEL = { selling: 'Selling', appraisal: 'Appraisal', find_agent: 'Find agent', fees: 'Fees', buying: 'Buying', other: 'Other', unknown: 'Unknown' };
const FILTERS = [{ value: 'all', label: 'All' }, { value: 'converted', label: 'Converted' }, { value: 'open', label: 'Not converted' }];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const firstVisitorLine = (s) => s.messages?.find((m) => m.role === 'user')?.content || '(no messages)';

const ChatStat = ({ icon: Icon, label, value, tone }) => (
  <div className="stat-card">
    <div className={`stat-icon stat-icon-${tone}`}><Icon size={22} /></div>
    <div className="stat-content">
      <p className="stat-label">{label}</p>
      <p className="stat-value" data-testid={`chat-stat-${label.replace(/\W+/g, '-').toLowerCase()}`}>{value}</p>
    </div>
  </div>
);

const Transcript = ({ session, onDelete }) => {
  if (!session) {
    return <div className="chat-admin-empty" data-testid="chat-transcript-empty">Select a conversation to read the transcript</div>;
  }
  const lead = session.lead || {};
  return (
    <div className="chat-admin-transcript" data-testid="chat-transcript">
      <div className="chat-admin-transcript-head">
        <div>
          <h2 className="chat-admin-transcript-title">{lead.fullName || 'Anonymous visitor'}</h2>
          <p className="chat-admin-transcript-meta">
            {fmt(session.createdAt)} · {session.messages?.length || 0} messages · landed on <code>{session.page || '/'}</code>
            {lead.intent && <> · intent <strong>{INTENT_LABEL[lead.intent] || lead.intent}</strong></>}
          </p>
        </div>
        <div className="chat-admin-transcript-actions">
          {session.converted
            ? <span className="lead-source-badge is-converted" data-testid="chat-converted-badge">Lead captured</span>
            : <span className="lead-source-badge is-test" data-testid="chat-open-badge">No lead yet</span>}
          <button type="button" className="chat-admin-delete-btn" onClick={() => onDelete([session.session_id])} data-testid="chat-delete-one">
            <Trash2 size={16} /> Delete
          </button>
        </div>
      </div>

      {(lead.propertyAddress || lead.mobile || lead.email || lead.notes) && (
        <div className="chat-admin-lead-grid" data-testid="chat-lead-summary">
          {lead.propertyAddress && <div><label>Address</label><p>{lead.propertyAddress}</p></div>}
          {lead.mobile && <div><label>Mobile</label><p>{lead.mobile}</p></div>}
          {lead.email && <div><label>Email</label><p>{lead.email}</p></div>}
          {lead.notes && <div className="span-2"><label>Mia's summary</label><p>{lead.notes}</p></div>}
          {session.contactId && (
            <div className="span-2"><label>Lead record</label><p><a href={`/admin/contacts/${session.contactId}`} data-testid="chat-open-lead-link">Open contact →</a></p></div>
          )}
        </div>
      )}

      <div className="chat-admin-bubbles">
        {(session.messages || []).map((m, i) => (
          <div key={i} className={`chat-msg chat-msg-${m.role}`} data-testid={`transcript-msg-${i}`}>
            <span className="chat-admin-bubble-time">{m.role === 'user' ? 'Visitor' : 'Mia'} · {m.ts ? new Date(m.ts).toLocaleTimeString('en-AU', { timeStyle: 'short' }) : ''}</span>
            {m.content}
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminChats = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [sending, setSending] = useState(false);
  const { selected, toggle, toggleAll, clear } = useRowSelection();

  useEffect(() => {
    adminAPI.getChats()
      .then((d) => { setSessions(d.sessions); setActiveId(d.sessions[0]?.session_id || null); })
      .catch((e) => { if (e.response?.status === 401) logout(); toast.error('Failed to load chats'); })
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => sessions.filter((s) => {
    if (filter === 'converted' && !s.converted) return false;
    if (filter === 'open' && s.converted) return false;
    if (!search) return true;
    const hay = `${s.lead?.fullName || ''} ${s.lead?.email || ''} ${s.lead?.propertyAddress || ''} ${(s.messages || []).map((m) => m.content).join(' ')}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  }), [sessions, filter, search]);

  const converted = sessions.filter((s) => s.converted).length;
  const rate = sessions.length ? Math.round((converted / sessions.length) * 100) : 0;
  const active = sessions.find((s) => s.session_id === activeId) || null;

  const removeSessions = async (ids) => {
    const { deleted } = await adminAPI.deleteChats(ids);
    setSessions((prev) => prev.filter((s) => !ids.includes(s.session_id)));
    if (ids.includes(activeId)) setActiveId(null);
    clear();
    return deleted;
  };

  const deleteOne = async (ids) => {
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    const n = await removeSessions(ids);
    toast.success(`Deleted ${n} conversation${n === 1 ? '' : 's'}`);
  };

  const sendInsights = async () => {
    setSending(true);
    try {
      const { to } = await adminAPI.sendInsightsNow();
      toast.success(`Weekly insights email is on its way to ${to}`);
    } catch (e) {
      toast.error('Could not send the insights email');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="admin-loading">Loading conversations...</div>;

  const visibleIds = visible.map((s) => s.session_id);

  return (
    <div className="admin-page" data-testid="admin-chats-page">
      <div className="admin-header">
        <div>
          <button onClick={() => navigate('/admin/dashboard')} className="admin-back-btn"><ArrowLeft size={20} /> Back to Dashboard</button>
          <h1 className="admin-page-title">Chat Conversations</h1>
          <p className="admin-page-subtitle">Every conversation visitors have had with Mia</p>
        </div>
        <div className="admin-header-actions">
          <button type="button" className="admin-action-btn admin-action-secondary chat-admin-insights-btn" onClick={sendInsights} disabled={sending} data-testid="send-insights-now-btn">
            <Mail size={16} /> {sending ? 'Sending…' : 'Email me this week\'s insights'}
          </button>
          <button onClick={logout} className="admin-logout-btn">Logout</button>
        </div>
      </div>

      <div className="stats-grid chat-admin-stats">
        <ChatStat icon={MessageSquare} label="Conversations" value={sessions.length} tone="primary" />
        <ChatStat icon={CheckCircle2} label="Leads captured" value={converted} tone="success" />
        <ChatStat icon={Percent} label="Conversion rate" value={`${rate}%`} tone="warning" />
      </div>

      <div className="admin-search">
        <input
          type="text"
          placeholder="Search names, emails, addresses or anything said in chat..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="admin-search-input"
          data-testid="chats-search-input"
        />
        <div className="lead-status-filter" role="tablist" data-testid="chat-filter">
          {FILTERS.map((f) => (
            <button key={f.value} type="button" role="tab" aria-selected={filter === f.value}
              className={`lead-status-filter-btn${filter === f.value ? ' is-active' : ''}`}
              onClick={() => setFilter(f.value)} data-testid={`chat-filter-${f.value}`}>
              {f.label}
              <span className="lead-status-filter-count">
                {f.value === 'all' ? sessions.length : f.value === 'converted' ? converted : sessions.length - converted}
              </span>
            </button>
          ))}
        </div>
      </div>

      <DeleteSelectedBar count={selected.size} label="conversation" onConfirm={() => removeSessions([...selected])} onClear={clear} />

      <div className="chat-admin-layout">
        <div className="chat-admin-list" data-testid="chat-session-list">
          <div className="chat-admin-list-head">
            <input type="checkbox" aria-label="Select all visible conversations" data-testid="select-all-chats"
              checked={visibleIds.length > 0 && visibleIds.every((id) => selected.has(id))} onChange={() => toggleAll(visibleIds)} />
            <span>{visible.length} shown</span>
          </div>
          {visible.length === 0 && <div className="admin-table-empty">No conversations match</div>}
          {visible.map((s) => (
            <div key={s.session_id} className={`chat-admin-row${s.session_id === activeId ? ' is-active' : ''}`}
              onClick={() => setActiveId(s.session_id)} data-testid={`chat-session-${s.session_id}`}>
              <input type="checkbox" checked={selected.has(s.session_id)} onChange={() => toggle(s.session_id)} onClick={(e) => e.stopPropagation()}
                aria-label="Select conversation" data-testid={`select-chat-${s.session_id}`} />
              <div className="chat-admin-row-body">
                <div className="chat-admin-row-top">
                  <strong>{s.lead?.fullName || 'Anonymous visitor'}</strong>
                  {s.converted && <span className="lead-source-badge is-converted">Lead</span>}
                  {s.lead?.intent && s.lead.intent !== 'unknown' && <span className="lead-source-badge is-chat">{INTENT_LABEL[s.lead.intent] || s.lead.intent}</span>}
                </div>
                <p className="chat-admin-row-preview">{firstVisitorLine(s)}</p>
                <span className="chat-admin-row-meta">{fmt(s.createdAt)} · {s.messages?.length || 0} msgs · {s.page || '/'}</span>
              </div>
            </div>
          ))}
        </div>
        <Transcript session={active} onDelete={deleteOne} />
      </div>
    </div>
  );
};

export default AdminChats;
